import React, { useEffect, useRef, useState } from "react";
import "./AeonEntry.css";

const BOOT_STAGES = [
  { code: "01", label: "Private node handshake", progress: 18 },
  { code: "02", label: "Attaching market feeds", progress: 43 },
  { code: "03", label: "Calibrating signal layer", progress: 72 },
  { code: "04", label: "Opening the observatory", progress: 96 },
];

function SignalCore({ compact = false }) {
  return (
    <div className={`ae-entry-core ${compact ? "is-compact" : ""}`} aria-hidden="true">
      <div className="ae-entry-orbit orbit-one"><i /><i /><i /></div>
      <div className="ae-entry-orbit orbit-two"><i /><i /></div>
      <div className="ae-entry-orbit orbit-three"><i /></div>
      <div className="ae-entry-gyro">
        <div className="ae-entry-cube">
          <span className="cube-face cube-front">A</span>
          <span className="cube-face cube-back">06</span>
          <span className="cube-face cube-right" />
          <span className="cube-face cube-left" />
          <span className="cube-face cube-top" />
          <span className="cube-face cube-bottom" />
        </div>
      </div>
      <span className="ae-entry-axis axis-x" />
      <span className="ae-entry-axis axis-y" />
      <span className="ae-entry-core-pulse" />
    </div>
  );
}

function MarketCoordinates() {
  return (
    <div className="ae-entry-coordinates" aria-hidden="true">
      <span className="coord-btc"><i /> BTC / 000.00</span>
      <span className="coord-eth"><i /> ETH / 120.00</span>
      <span className="coord-sol"><i /> SOL / 240.00</span>
      <span className="coord-vol">VOL SURFACE / LIVE</span>
    </div>
  );
}

export function AeonAccessGate({ onUnlock }) {
  const [value, setValue] = useState("");
  const [denied, setDenied] = useState(false);
  const [checking, setChecking] = useState(false);
  const rootRef = useRef(null);

  async function attempt(event) {
    event?.preventDefault();
    if (checking || !value) return;
    setChecking(true);
    setDenied(false);
    try {
      // The access phrase is verified server-side; it never ships in this bundle.
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: value }),
      });
      if (res.ok) {
        const data = await res.json();
        onUnlock(data.token);
        return;
      }
    } catch {
      // fall through to denied state
    } finally {
      setChecking(false);
    }
    setDenied(false);
    window.requestAnimationFrame(() => setDenied(true));
    setValue("");
  }

  function moveScene(event) {
    if (!rootRef.current || event.pointerType === "touch") return;
    const x = (event.clientX / window.innerWidth - 0.5) * 2;
    const y = (event.clientY / window.innerHeight - 0.5) * 2;
    rootRef.current.style.setProperty("--pointer-x", x.toFixed(3));
    rootRef.current.style.setProperty("--pointer-y", y.toFixed(3));
  }

  return (
    <main ref={rootRef} className="ae-entry ae-entry-access" onPointerMove={moveScene}>
      <div className="ae-entry-noise" aria-hidden="true" />
      <div className="ae-entry-horizon" aria-hidden="true" />
      <div className="ae-entry-grid" aria-hidden="true" />

      <header className="ae-entry-header">
        <div className="ae-entry-brand">
          <span>A<i>·</i></span>
          <div>
            <strong>AEON</strong>
            <small>Market intelligence</small>
          </div>
        </div>
        <div className="ae-entry-node">
          <i />
          <span>Private node</span>
          <strong>06 / ONLINE</strong>
        </div>
      </header>

      <section className="ae-entry-statement">
        <span className="ae-entry-overline">Market observatory / restricted access</span>
        <h1>Markets<br />never <em>sleep.</em></h1>
        <p>
          One operational view of price, risk, and machine judgment.
          Built for decisions made while the tape is moving.
        </p>
      </section>

      <div className="ae-entry-scene">
        <SignalCore />
        <MarketCoordinates />
      </div>

      <form className={`ae-entry-auth ${denied ? "is-denied" : ""}`} onSubmit={attempt}>
        <div className="ae-entry-auth-head">
          <span>Operator access</span>
          <small>AES / LOCAL SESSION</small>
        </div>
        <label htmlFor="aeon-access-key">Access phrase</label>
        <div className="ae-entry-input">
          <span>›</span>
          <input
            id="aeon-access-key"
            type="password"
            value={value}
            autoFocus
            autoComplete="current-password"
            onChange={(event) => {
              setValue(event.target.value);
              setDenied(false);
            }}
            placeholder="ENTER KEY"
            aria-invalid={denied}
          />
          <i>{String(value.length).padStart(2, "0")}</i>
        </div>
        <div className="ae-entry-auth-feedback" aria-live="polite">
          {denied ? "ACCESS DENIED / CHECK PHRASE" : "ENCRYPTED HANDSHAKE READY"}
        </div>
        <button type="submit"><span>Enter Aeon</span><i>↗</i></button>
      </form>

      <footer className="ae-entry-footer">
        <span>41.8781° N / 87.6298° W</span>
        <span>Evidence before conviction</span>
        <span>UTC−05 / CENTRAL</span>
      </footer>
    </main>
  );
}

export default function AeonLoader({ message = "Assembling market intelligence" }) {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setStage((current) => Math.min(current + 1, BOOT_STAGES.length - 1));
    }, 560);
    return () => window.clearInterval(timer);
  }, []);

  const activeStage = BOOT_STAGES[stage];

  return (
    <main className="ae-entry ae-entry-boot">
      <div className="ae-entry-noise" aria-hidden="true" />
      <div className="ae-entry-horizon" aria-hidden="true" />
      <div className="ae-entry-grid" aria-hidden="true" />

      <header className="ae-entry-header">
        <div className="ae-entry-brand">
          <span>A<i>·</i></span>
          <div>
            <strong>AEON</strong>
            <small>System wake sequence</small>
          </div>
        </div>
        <div className="ae-entry-node">
          <i />
          <span>Signal core</span>
          <strong>INITIALIZING</strong>
        </div>
      </header>

      <section className="ae-boot-copy">
        <span>AEON / WAKE</span>
        <h1>{message}</h1>
        <p>Do not close this window.</p>
      </section>

      <div className="ae-boot-scene">
        <SignalCore compact />
        <div className="ae-boot-radar" aria-hidden="true"><i /><i /><i /><i /></div>
      </div>

      <section className="ae-boot-console">
        <div className="ae-boot-current">
          <span>{activeStage.code}</span>
          <strong>{activeStage.label}</strong>
          <small>{activeStage.progress}%</small>
        </div>
        <div className="ae-boot-progress"><i style={{ width: `${activeStage.progress}%` }} /></div>
        <div className="ae-boot-stages">
          {BOOT_STAGES.map((item, index) => (
            <span key={item.code} className={index <= stage ? "is-active" : ""}>
              {item.code} / {item.label}
            </span>
          ))}
        </div>
      </section>

      <footer className="ae-entry-footer">
        <span>LOCAL CHANNEL / SECURE</span>
        <span>Calibrating against live conditions</span>
        <span>NODE 06</span>
      </footer>
    </main>
  );
}
